import React from "react";
import {
  ArrowLeftOnRectangleIcon,
  BellIcon,
  MagnifyingGlassIcon,
  Cog8ToothIcon,
  ClipboardDocumentListIcon,
  ChatBubbleLeftEllipsisIcon,
  ChevronDownIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/24/outline";
import Avatar from "../../shared/Avatar";
import Dropdown from "../../shared/Dropdown";
import Header from "../../shared/Header";
import Input from "../../shared/Input";
import { useAuth } from "../../hooks/useAuth";
import { useContext } from "react";
import { LayoutContext } from "../Root";
import defaultSettings from "../../config/defaultSettings";
import { Badge } from "../../shared";
import { useConfig } from "../../hooks";
import { useNavigate } from "react-router-dom";
// import { NotificationBell } from "../../shared/NotificationBell"; // Using tabbed interface instead
import { useNotifications } from "../../contexts/NotificationContext";
import { Tabs } from "../../shared/Tabs";
import NotificationContent from "./NotificationsContent";
import { UniversalCreateButton } from "../../components/UniversalCreateButton";
// import TaskShow from "@/pages/Tasks/TaskShow";

/**
 * Creates notification tabs config with close callback for navigation
 * @param {Function} onClose - Callback to close the dropdown after navigation
 * @returns {Array} Tabs configuration
 */
const getNotificationTabs = (onClose) => [
  {
    id: "Notifications",
    content: <NotificationContent onNavigate={onClose} />,
  },
  // {
  //   id: "Tasks",
  //   content: <TaskShow />,
  // },
];

const OPEN_HELP_HUB_EVENT = "attune:help-hub-open";

const LayoutHeader = (props) => {
  const { logout, user } = useAuth();
  const { configs } = useConfig();
  const { breakpoint } = useContext(LayoutContext);
  const navigate = useNavigate();
  const { stats } = useNotifications();

  const options = [
    // {
    //   icon: <UserIcon className="w-4 h-4" />,
    //   title: "Account",
    //   path: "/account",
    // },
    {
      icon: <Cog8ToothIcon className="w-4 h-4" />,
      title: "Account Settings",
      path: "/account/information",
    },
    // {
    //   icon: <WrenchScrewdriverIcon className="w-4 h-4" />,
    //   title: "Site Settings",
    //   onClick: props.openDrawer,
    // },
    {
      icon: <ClipboardDocumentListIcon className="w-4 h-4" />,
      title: "Admin Menu",
      path: "/admin",
    },
    {
      icon: <ChatBubbleLeftEllipsisIcon className="w-4 h-4" />,
      title: "Submit Ticket",
      path: "/feedback?feedbackTabs=submit",
    },
    {
      icon: <QuestionMarkCircleIcon className="w-4 h-4" />,
      title: "Help & Onboarding",
      onClick: () => {
        if (typeof window === "undefined") return;
        window.dispatchEvent(
          new CustomEvent(OPEN_HELP_HUB_EVENT)
        );
      },
    },
    {
      title: "Logout",
      icon: <ArrowLeftOnRectangleIcon className="w-4 h-4" />,
      section: true,
      onClick: () => logout(),
    },
  ].filter(({ path }) => {
    if (path === "/admin" && props.user?.authority !== "superAdmin") {
      return false;
    }
    return true;
  });

  const searchIcon = (
    <div className="h-full w-full flex items-center justify-center cursor-pointer">
      <MagnifyingGlassIcon className="stroke-2 h-5 w-5" />
    </div>
  );

  const avatar = (
    // <div className="flex flex-row items-center h-full cursor-pointer">
    //   <Avatar src={user.avatar} size="sm" />
    //   <span className="ml-1 hidden md:block">
    //     <ChevronDownIcon className="w-4 h-4" />
    //   </span>
    // </div>
    <div className="flex flex-row items-center justify-center h-full w-full cursor-pointer">
      <Avatar src={user.avatar} size="xs" />
      <span className="ml-1 hidden md:block">
        <ChevronDownIcon className="w-4 h-4" />
      </span>
    </div>
  );

  const bell = (
    <div className="flex flex-row items-center justify-center h-full w-full cursor-pointer relative">
      <BellIcon className="stroke-2 h-5 w-5 text-gray-700" />
      {stats?.unread > 0 && (
        <span className="absolute top-1 right-1 inline-flex items-center justify-center w-4 h-4 text-xs font-bold text-white bg-red-500 rounded-full pointer-events-none">
          {stats.unread > 99 ? "99+" : stats.unread}
        </span>
      )}
    </div>
  );

  /**
   * Notification dropdown content as a function to receive close callback from Dropdown
   * This allows NotificationContent to close the dropdown after navigation
   */
  const notifications = (close) => <Tabs data={getNotificationTabs(close)} />;

  const searchBar = (
    <div className="overflow-x-auto w-80 p-2 h-full cursor">
      <Input clearable size="sm" placeholder="Search..." />
    </div>
  );

  const openHelpHub = () => {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent(OPEN_HELP_HUB_EVENT));
  };

  const isSmall = breakpoint === "sm" || breakpoint === "xs";

  const rightContent = [
    // <Dropdown
    //   isMobile={isSmall}
    //   key={1}
    //   data={searchBar}
    //   trigger={searchIcon}
    // />,
    <div
      key="tutorial-trigger"
      onClick={openHelpHub}
      className="h-full flex items-center cursor-pointer"
      title="Open help and onboarding hub"
    >
      <QuestionMarkCircleIcon className="stroke-2 h-5 w-5" />
    </div>,
    <div key="universal-create" data-tour="create-button" className="h-full">
      <UniversalCreateButton />
    </div>,
    <div key="notifications" data-tour="notifications" className="h-full">
      <Dropdown
        widthClasses="w-96"
        isMobile={isSmall}
        data={notifications}
        trigger={bell}
        className="flex"
      />
    </div>,
    <div key="account-menu" data-tour="account-menu" className="h-full">
      <Dropdown
        closeOnClick
        data={options}
        trigger={avatar}
        className="flex"
        width={14}
      />
    </div>,
  ];

  const envBadge = defaultSettings.STAGE !== "prod" && (
    <div className="flex flex-row items-center space-x-1">
      <Badge className="uppercase" label={defaultSettings.STAGE} />
      {configs?.parentCompany?.appType && (
        <Badge
          color="blue"
          className="uppercase"
          label={configs?.parentCompany?.appType}
        />
      )}
      {configs?.parentCompany?.industry && (
        <Badge
          color="orange"
          className="uppercase"
          label={configs?.parentCompany?.industry}
        />
      )}
    </div>
  );

  return <Header leftContent={envBadge} rightContent={rightContent} />;
};

export default LayoutHeader;
